import {
	BN,
	OrderType,
	MarketType,
	PositionDirection,
	PostOnlyParams,
	OrderTriggerCondition,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS } from '@velocity-exchange/common';

export interface ServerAuctionParamsResponse {
	orderType?: string;
	marketType?: string;
	userOrderId?: number;
	direction?: string;
	baseAssetAmount?: string | number;
	marketIndex?: number;
	reduceOnly?: boolean;
	postOnly?: string;
	immediateOrCancel?: boolean;
	triggerPrice?: string | number | null;
	triggerCondition?: string;
	oraclePriceOffset?: string | number;
	auctionDuration?: number;
	maxTs?: string | number | null;
	auctionStartPrice?: string | number;
	auctionEndPrice?: string | number;
	// Add other fields as needed based on server response
}

export interface MappedAuctionParams {
	orderType: OrderType;
	marketType: MarketType;
	userOrderId?: number;
	direction: PositionDirection;
	baseAssetAmount: BN;
	marketIndex: number;
	reduceOnly?: boolean;
	postOnly?: PostOnlyParams;
	immediateOrCancel?: boolean;
	triggerPrice?: BN | null;
	triggerCondition?: OrderTriggerCondition;
	oraclePriceOffset?: BN;
	auctionDuration: number | undefined;
	maxTs?: BN | null;
	auctionStartPrice: BN | undefined;
	auctionEndPrice: BN | undefined;
}

// Field mapping configuration
type FieldType = 'enum' | 'bn' | 'number' | 'boolean' | 'bn_nullable';

interface FieldConfig {
	type: FieldType;
}

const FIELD_MAPPING: Record<keyof ServerAuctionParamsResponse, FieldConfig> = {
	// Enums (string -> enum object)
	orderType: { type: 'enum' },
	marketType: { type: 'enum' },
	direction: { type: 'enum' },
	postOnly: { type: 'enum' },
	triggerCondition: { type: 'enum' },

	// Numbers (keep as numbers)
	userOrderId: { type: 'number' },
	marketIndex: { type: 'number' },
	auctionDuration: { type: 'number' },

	// Booleans
	reduceOnly: { type: 'boolean' },
	immediateOrCancel: { type: 'boolean' },

	// BNs (string/number -> BN)
	baseAssetAmount: { type: 'bn' },
	auctionStartPrice: { type: 'bn' },
	auctionEndPrice: { type: 'bn' },

	// Nullable BNs
	oraclePriceOffset: { type: 'bn_nullable' },
	triggerPrice: { type: 'bn_nullable' },
	maxTs: { type: 'bn_nullable' },
};

// Type conversion functions
const convertValue = (value: any, type: FieldType): any => {
	switch (type) {
		case 'enum':
			return ENUM_UTILS.toObj(value);
		case 'bn':
			if (value === null || value === undefined) {
				// Server returned null for a required BN field, this should cause fallback to client-side calculation
				throw new Error(
					`Server returned ${
						value === null ? 'null' : 'undefined'
					} for required BN field, expected non-null value`
				);
			}
			return new BN(value.toString());
		case 'bn_nullable':
			return value !== undefined && value !== null
				? new BN(value.toString())
				: null;
		case 'number':
			return value;
		case 'boolean':
			return value;
		default:
			return value;
	}
};

/**
 * Maps the server response from getOrderParams back to proper client-side types
 */
export function mapAuctionParamsResponse(
	serverResponse: ServerAuctionParamsResponse
): MappedAuctionParams {
	const mapped: Partial<MappedAuctionParams> = {};

	// Process each field based on its configuration
	Object.entries(FIELD_MAPPING).forEach(([fieldName, config]) => {
		const serverValue =
			serverResponse[fieldName as keyof ServerAuctionParamsResponse];

		if (serverValue !== undefined) {
			try {
				(mapped as any)[fieldName] = convertValue(serverValue, config.type);
			} catch (error) {
				console.error(`🔴 Field conversion error:`, {
					fieldName,
					serverValue,
					expectedType: config.type,
					actualType: typeof serverValue,
					isNull: serverValue === null,
					isUndefined: serverValue === undefined,
					fullServerResponse: serverResponse,
				});
				throw new Error(
					`Failed to convert field '${fieldName}' (value: ${serverValue}, type: ${
						config.type
					}): ${error instanceof Error ? error.message : error}`
				);
			}
		}
	});

	return mapped as MappedAuctionParams;
}
